import { fetchFlights } from 'actions/FlightActions'
import CurrencyContext from 'contexts/currencyContext'
import { calculateFlightLuxPlusMemberSavings, getFlightSearchKey, getFlightSearchLuxPlusCacheKey } from 'lib/flights/flightUtils'
import { useContext, useEffect, useMemo } from 'react'
import { EmptyObject } from 'lib/object/objectUtils'
import { arrayToMap, EmptyArray, nonNullable } from 'lib/array/arrayUtils'
import { useAppDispatch, useAppSelector } from './reduxHooks'
import { FlightViewTypes } from 'constants/flight'
import type { FlightSearchItinerary } from 'api/flights'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { canRequestFlightLuxPlusPricing } from 'lib/flights/flightLuxPlusPricing'
import { getTwoOneWayCompatibilityGroup } from 'lib/flights/domesticFlightRanking'

export interface FlightSearchParams {
  excludeProviders?: Array<string>
  itinerary: Array<FlightSearchItinerary>
  occupancies: Array<App.Occupants>
  fareClass: string
  maxArrivalTime?: string
  minDepartureTime?: string
  minReturningDepartureTime?: string
  carriers?: Array<string>
  carrier?: string
  fareType?: string
  viewType?: string
  flightsCredit?: boolean
  forceBundleId?: string
  source?: string
  filterAirlines?: Array<string>
  key?: string | number
  dealId?: string
  allowIncompleteJourneys?: boolean
  charterSearchId?: string
  accommodationCheckInDate?: string
  email?: string
  feederOriginAirportCode?: string
  /** Forwards `isFeederBundle: true` to /flightsearch (FLY-5300). Set on feeder leg search only. */
  isFeederBundle?: boolean
}

interface Options {
  disabled?: boolean
  segments?: Array<string>
  activeSegmentKey?: string
  selectedFlights?: Record<string, App.JourneyV2>
  selectedFareFamilies?: Record<string, App.FlightFareFamily>
  offerId?: string
  offerTilePrice?: number
  offerCalendarPrice?: number
  isStandalone?: boolean
  isBundledFlow?: boolean
}

function useSearchFlights(
  params: FlightSearchParams,
  options: Options = EmptyObject,
): {
  journeys: Array<App.JourneyV2>
  fetching: boolean
  error: any
  providerSearchId: string | undefined
  viewType: string | undefined
  fnfEligible: boolean | undefined
  key: string
  metadata: App.FlightSearchMetadata | undefined
  searchResultsTimestamp: number | undefined
  combinedJourneys: Array<App.FlightCombinedJourney>
  memberPriceSaveAmount: number
  requestLuxPlusPricing: boolean
  /**
   * Raw fares for the segment adjacent to `activeSegmentKey` (the departing page's
   * returning fares, or vice versa) - already fetched in the same search response,
   * exposed so the not-yet-selected leg's cheapest fare can be estimated without a
   * second request.
   */
  otherSegmentJourneys: Array<App.JourneyV2>
} {
  const currency = useContext(CurrencyContext)
  const {
    disabled,
    activeSegmentKey,
    segments = EmptyArray as Utils.Defined<Options['segments']>,
    selectedFareFamilies = EmptyObject as Utils.Defined<Options['selectedFareFamilies']>,
    selectedFlights = EmptyObject as Utils.Defined<Options['selectedFlights']>,
    offerTilePrice,
    offerId,
    offerCalendarPrice,
    isStandalone,
    isBundledFlow,
  } = options

  const paramsWithCurrency = {
    ...params,
    currency,
  }
  const dispatch = useAppDispatch()

  const enableSabreContent = useFeatureFlag(OptimizelyFeatureFlags.flightsEnableSabreContent)
  const standaloneLuxPlusPricingEnabled = useFeatureFlag(OptimizelyFeatureFlags.flightsLuxPlusPricing)
  const bundledLuxPlusPricingEnabled = useFeatureFlag(OptimizelyFeatureFlags.flightsBundledLuxPlusPricing)
  const enableStandaloneLuxPlusPricing = !!isStandalone && standaloneLuxPlusPricingEnabled
  const enableBundledLuxPlusPricing = !!isBundledFlow && bundledLuxPlusPricingEnabled

  const enableLuxPlusPricing = useMemo(() => {
    if (isBundledFlow) {
      return !!enableBundledLuxPlusPricing
    }
    return !!enableStandaloneLuxPlusPricing
  }, [isBundledFlow, enableBundledLuxPlusPricing, enableStandaloneLuxPlusPricing])

  const requestLuxPlusPricing = useAppSelector(state => canRequestFlightLuxPlusPricing(state, {
    enabled: enableLuxPlusPricing,
    isBundledFlow,
  }))

  const searchCacheKey = getFlightSearchLuxPlusCacheKey(params.key, requestLuxPlusPricing)
  const paramsWithCurrencyAndCacheKey = {
    ...paramsWithCurrency,
    key: searchCacheKey,
  }
  const key = getFlightSearchKey(paramsWithCurrencyAndCacheKey)
  const hasResultCached = useAppSelector(state => !!(state.flights.searchV2Flights[key] || state.flights.searchFlights[key]))

  useEffect(() => {
    if (!disabled && !hasResultCached) {
      dispatch(fetchFlights(paramsWithCurrencyAndCacheKey, { offerTilePrice, offerId, offerCalendarPrice, enableSabreContent, enableLuxPlusPricing, isBundledFlow }))
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [key, disabled, hasResultCached])

  const flightsV2 = useAppSelector(state => state.flights.searchV2Flights[key])

  const v2Journeys = useMemo((): Array<App.JourneyV2> => {
    if (flightsV2 && segments && activeSegmentKey) {
      const segmentIndex = segments.findIndex(segment => segment === activeSegmentKey)

      if (segmentIndex === 0) {
        return flightsV2.fares[segmentIndex]
      } else {
        const currentFares = flightsV2.fares[segmentIndex] || []
        const prevFares = flightsV2.fares[segmentIndex - 1]
        const prevSegment = segments[segmentIndex - 1]
        const prevFlight = selectedFlights[prevSegment]
        const prevFareFamily = selectedFareFamilies[prevSegment]

        // If no previous flight is selected (e.g., charter flights display), return all fares for this segment
        if (!prevFlight) {
          return currentFares
        }

        if ([FlightViewTypes.RETURN, FlightViewTypes.MULTI_CITY].includes(prevFlight?.fareType as FlightViewTypes)) {
          let currentPrevFlight = prevFlight

          // if a departing fare family is selected, we need to find the corresponding departing flight to get the correct returning flights
          if (prevFareFamily) {
            const correspondingFares = prevFares.filter((fare) => {
              const isSameCarrier = fare.carrier === prevFlight.carrier
              const isSameFareType = fare.fareType === prevFlight.fareType
              const sameFlightsLength = fare.flightGroup.flights.length === prevFlight.flightGroup.flights.length
              const isSameBookingClass = fare.bookingInfo.bookingClass === prevFareFamily.bookingClass
              const isSameFlight = fare.flightGroup.flights.every((flight, index) => {
                return flight.departingTime === prevFlight.flightGroup.flights[index].departingTime &&
                  flight.arrivalTime === prevFlight.flightGroup.flights[index].arrivalTime
              })

              return isSameCarrier && isSameFareType && sameFlightsLength && isSameFlight && isSameBookingClass
            })

            currentPrevFlight = correspondingFares[0] || prevFlight
          }

          // We have a departing flight that expects a return leg, find all return flights that pair with this one
          const currentFaresById = arrayToMap(currentFares, fare => fare.bookingInfo.boookingKey)
          return nonNullable(currentPrevFlight.pairedFareId.map(id => currentFaresById.get(id)))
        } else {
          const prevContentGroup = getTwoOneWayCompatibilityGroup(prevFlight?.bookingInfo.priceType)

          // Doesn't have a paired departing flight, so we must be looking for one ways only
          return currentFares?.filter((fare) => {
            return fare.fareType !== FlightViewTypes.RETURN && getTwoOneWayCompatibilityGroup(fare.bookingInfo.priceType) === prevContentGroup
          })
        }
      }
    }

    return []
  }, [flightsV2, segments, activeSegmentKey, selectedFlights, selectedFareFamilies])

  const memberPriceSaveAmount = useMemo(
    () => calculateFlightLuxPlusMemberSavings(flightsV2?.fares ?? []),
    [flightsV2?.fares],
  )

  const otherSegmentJourneys = useMemo((): Array<App.JourneyV2> => {
    if (!flightsV2 || !segments || !activeSegmentKey) return []

    const segmentIndex = segments.findIndex(segment => segment === activeSegmentKey)
    // Only meaningful for the exact two-segment (departing/returning) case this is built
    // for, where the "other" index is unambiguous. An unmatched `activeSegmentKey` (-1) or
    // a 3rd+ segment has no well-defined other index, so bail out rather than silently
    // returning the wrong (or even the current) segment's fares.
    if (segmentIndex < 0 || segments.length !== 2) return []

    const otherSegmentIndex = segmentIndex === 0 ? 1 : 0

    return flightsV2.fares[otherSegmentIndex] ?? []
  }, [flightsV2, segments, activeSegmentKey])

  return {
    journeys: v2Journeys || [],
    fetching: !disabled && (!flightsV2 || !!flightsV2.fetching),
    error: flightsV2?.error,
    providerSearchId: flightsV2?.searchId,
    viewType: flightsV2?.viewType,
    fnfEligible: flightsV2?.fnfEligible,
    key,
    metadata: flightsV2?.metadata,
    searchResultsTimestamp: flightsV2?.timestamp,
    combinedJourneys: flightsV2?.combinedJourneys ?? [],
    memberPriceSaveAmount,
    // Whether LuxPlus+ pricing was actually requested for this search. Needed to tell "no member
    // price because we never asked" apart from "asked and none came back" when reporting.
    requestLuxPlusPricing,
    otherSegmentJourneys,
  }
}

export default useSearchFlights
